/**
 * Powód, dla którego wygenerowany raport nie ma ani jednej strony.
 *
 * Backend (SuperTacho.Printout/EmptyDocumentReason.cs) zapisuje kod EMPTY_DOCUMENT_*
 * w ImportTask.Message i zwraca go jako `code` z /config-api/importTask/downloadOutputFile,
 * żeby zamiast ogólnego „brak wystarczających danych" pokazać użytkownikowi,
 * czego dokładnie zabrakło i co z tym zrobić.
 */
export const EMPTY_DOCUMENT_PREFIX = 'EMPTY_DOCUMENT'

export const isEmptyDocumentCode = (value?: string | null): value is string =>
  typeof value === 'string' && value.trim().startsWith(EMPTY_DOCUMENT_PREFIX)

const MISSING_TRANSLATION_MARKER = 'Missing translationId'

/**
 * Tłumaczy kod błędu na komunikat. Gdy tłumaczenia brak — translate() oddaje sam klucz
 * albo „Missing translationId: …" — schodzimy do komunikatu ogólnego.
 */
export const translateErrorCode = (
  translate: (key: string) => unknown,
  code?: string | null,
): string => {
  const fallback = () => String(translate(`errorCode.${EMPTY_DOCUMENT_PREFIX}`))

  const trimmed = (code ?? '').trim()
  if (!trimmed) {
    return fallback()
  }

  const key = `errorCode.${trimmed}`
  const translated = String(translate(key) ?? '')

  return !translated ||
    translated === key ||
    translated.includes(MISSING_TRANSLATION_MARKER)
    ? fallback()
    : translated
}

/** Pojedynczy brak zdiagnozowany przez generator: dla kogo, za jaki okres i czego zabrakło. */
type EmptyDocumentDetail = {
  name?: string
  period?: string
  reason?: string
}

type EmptyDocumentDiagnosis = {
  code?: string
  details?: EmptyDocumentDetail[]
}

// Backend serializuje PascalCase (Newtonsoft, domyślna konfiguracja), ale nie zakładamy tego na sztywno.
const readProp = (source: any, name: string) =>
  source?.[name] ?? source?.[name.charAt(0).toUpperCase() + name.slice(1)]

export const parseEmptyDocumentDiagnosis = (
  raw?: string | null,
): EmptyDocumentDiagnosis | null => {
  if (!raw || typeof raw !== 'string' || !raw.trim().startsWith('{')) {
    return null
  }

  try {
    const parsed = JSON.parse(raw)
    const details = readProp(parsed, 'details')
    if (!Array.isArray(details)) {
      return null
    }

    return {
      code: readProp(parsed, 'code'),
      details: details.map((detail: any) => ({
        name: readProp(detail, 'name'),
        period: readProp(detail, 'period'),
        reason: readProp(detail, 'reason'),
      })),
    }
  } catch {
    return null
  }
}

// Przy dużym zaznaczeniu nie zalewamy użytkownika listą na kilkadziesiąt pozycji.
const MAX_LISTED_DETAILS = 8

const formatDetail = (
  translate: (key: string) => unknown,
  detail: EmptyDocumentDetail,
): string => {
  const who = [detail.name, detail.period].filter(Boolean).join(', ')
  const reasonKey = `emptyDocumentDetail.${(detail.reason ?? '').trim()}`
  const reason = detail.reason ? String(translate(reasonKey) ?? '') : ''
  const readableReason =
    !reason || reason === reasonKey || reason.includes(MISSING_TRANSLATION_MARKER)
      ? detail.reason ?? ''
      : reason

  return who ? `${who} — ${readableReason}` : readableReason
}

/**
 * Komunikat dla zakończonego zadania wydruku, które nie wyprodukowało pliku:
 * nagłówek „czego zabrakło" plus lista konkretów per kierowca, jeśli generator ją przysłał.
 *
 * `code` i `diagnosis` pochodzą z odpowiedzi na pobranie pliku, `taskMessage` / `taskLogs`
 * z samego zadania — kod ogólny z pierwszego źródła uzupełniamy precyzyjniejszym z drugiego.
 */
export const resolveEmptyDocumentDetails = (
  translate: (key: string) => unknown,
  code?: string | null,
  taskMessage?: string | null,
  diagnosis?: string | null,
  taskLogs?: string | null,
): string => {
  const parsed =
    parseEmptyDocumentDiagnosis(diagnosis) ?? parseEmptyDocumentDiagnosis(taskLogs)

  const reason =
    code && code !== EMPTY_DOCUMENT_PREFIX
      ? code
      : isEmptyDocumentCode(taskMessage)
        ? taskMessage
        : (parsed?.code ?? code)

  const headline = translateErrorCode(translate, reason)
  const details = parsed?.details ?? []
  if (details.length === 0) {
    return headline
  }

  const listed = details
    .slice(0, MAX_LISTED_DETAILS)
    .map((detail) => `• ${formatDetail(translate, detail)}`)

  const hidden = details.length - listed.length
  if (hidden > 0) {
    listed.push(
      `• ${String(translate('emptyDocumentDetail.more')).replace('{0}', String(hidden))}`,
    )
  }

  return [headline, ...listed].join('\n')
}
